import React, { useState, useEffect } from 'react';
import api, { BACKEND_URL } from '../services/api';


const AdmissionForm = ({ studentId, initialData, onSuccess, onCancel, defaultCountry, defaultVisaType }) => {
    const [formData, setFormData] = useState({
        full_name: '', phone: '', email: '', passport_no: '',
        date_of_birth: '', sex: '', nationality: '',
        father_name: '', father_contact: '',
        mother_name: '', mother_contact: '',
        permanent_address: '', temporary_address: '',
        guardian_name: '', guardian_relation: '', guardian_contact: '',
        abroad_study_country: defaultCountry || '', 
        visa_type: defaultVisaType || '', 
        passed_out_year: '', gpa: '',
        preparation_classes: [],
        form_fee_paid: false,
        photo: null
    });

    const [activeCountries, setActiveCountries] = useState([]);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [isEditMode, setIsEditMode] = useState(false);

    useEffect(() => {
        const setFormDataFromExisting = (data) => {
            setFormData({
                full_name: data.full_name || '',
                phone: data.phone || '',
                email: data.email || '',
                passport_no: data.passport_no || '',
                date_of_birth: data.date_of_birth ? data.date_of_birth.split('T')[0] : '',
                sex: data.sex || '',
                nationality: data.nationality || '',
                father_name: data.father_name || '',
                father_contact: data.father_contact || '',
                mother_name: data.mother_name || '',
                mother_contact: data.mother_contact || '',
                permanent_address: data.permanent_address || '',
                temporary_address: data.temporary_address || '',
                guardian_name: data.guardian_name || '',
                guardian_relation: data.guardian_relation || '',
                guardian_contact: data.guardian_contact || '',
                abroad_study_country: data.abroad_study_country || '',
                visa_type: data.visa_type || '',
                passed_out_year: data.passed_out_year || '',
                gpa: data.gpa || '',
                preparation_classes: Array.isArray(data.preparation_classes)
                    ? data.preparation_classes
                    : typeof data.preparation_classes === 'string' && data.preparation_classes.startsWith('{')
                        ? data.preparation_classes.replace(/[{}]/g, '').split(',').filter(Boolean)
                        : [],
                form_fee_paid: data.form_fee_paid || false,
                photo: null
            });
            if (data.photo_url) {
                setPhotoPreview(data.photo_url.startsWith('http') ? data.photo_url : `${BACKEND_URL}${data.photo_url}`);
            }
        };

        const fetchStudentData = async (id) => {
            try {
                const res = await api.get(`/students/${id}`);
                setFormDataFromExisting(res.data);
            } catch (err) {
                setError('Failed to load student data.');
            }
        };

        const fetchData = async () => {
            try {
                const countriesRes = await api.get('/countries');
                setActiveCountries(countriesRes.data.filter(c => c.status === 'active'));
            } catch (err) {
                console.error("Failed to load metadata", err);
            }
        };
        fetchData();

        // If studentId or initialData is provided, pre-fill the form
        if (studentId || initialData) {
            setIsEditMode(true);
            if (initialData) {
                setFormDataFromExisting(initialData);
            } else {
                fetchStudentData(studentId);
            }
        }
    }, [studentId, initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            if (name === 'form_fee_paid') {
                setFormData({ ...formData, [name]: checked });
            } else if (name === 'preparation_classes') {
                let updatedClasses = [...formData.preparation_classes];
                if (checked) {
                    if (!updatedClasses.includes(value)) updatedClasses.push(value);
                } else {
                    updatedClasses = updatedClasses.filter(c => c !== value);
                }
                setFormData({ ...formData, preparation_classes: updatedClasses });
            } else { // Handle visa_type checkbox as radio
                setFormData({ ...formData, [name]: value });
            }
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
                setError('Only JPG/JPEG/PNG images are allowed.');
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                setError('Image must be less than 2MB.');
                return;
            }
            setError('');
            setFormData({ ...formData, photo: file });
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});

        // Frontend Validations
        const errs = {};
        if (!formData.full_name?.trim()) errs.full_name = 'Full name is required';
        if (!formData.phone?.trim()) errs.phone = 'Phone number is required';
        else if (!/^\+?[\d\s-]{7,15}$/.test(formData.phone)) errs.phone = 'Valid phone number required';
        if (!formData.email?.trim()) errs.email = 'Email address is required';
        else if (!/^\S+@\S+\.\S+$/.test(formData.email)) errs.email = 'Valid email address required';
        if (!formData.passport_no?.trim()) errs.passport_no = 'Passport number is required';
        if (!formData.date_of_birth) errs.date_of_birth = 'Date of birth is required';
        if (!formData.sex) errs.sex = 'Gender is required';
        if (!formData.nationality?.trim()) errs.nationality = 'Nationality is required';
        if (!formData.father_name?.trim()) errs.father_name = "Father's name is required";
        if (!formData.father_contact?.trim()) errs.father_contact = "Father's contact is required";
        if (!formData.mother_name?.trim()) errs.mother_name = "Mother's name is required";
        if (!formData.mother_contact?.trim()) errs.mother_contact = "Mother's contact is required";
        if (!formData.permanent_address?.trim()) errs.permanent_address = 'Permanent address is required';
        if (!formData.temporary_address?.trim()) errs.temporary_address = 'Temporary address is required';
        if (!formData.abroad_study_country) errs.abroad_study_country = 'Target country is required';
        if (!formData.visa_type) errs.visa_type = 'Visa type is required';
        if (formData.visa_type === 'Student') {
            if (!formData.passed_out_year?.trim()) errs.passed_out_year = 'Passed out year is required';
            if (!formData.gpa?.trim()) errs.gpa = 'GPA / Score is required';
        }

        if (Object.keys(errs).length > 0) {
            setFieldErrors(errs);
            setError('Please fix the highlighted fields before submitting.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const submitData = new FormData();
        Object.keys(formData).forEach(key => {
            if (key === 'preparation_classes') {
                submitData.append(key, JSON.stringify(formData[key]));
            } else if (key === 'photo') {
                if (formData[key]) submitData.append(key, formData[key]);
            } else if (formData[key] !== null && formData[key] !== '') {
                submitData.append(key, formData[key]);
            }
        });

        try {
            if (isEditMode) {
                await api.put(`/students/${studentId || initialData.id}`, submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
                alert('Student profile updated successfully!');
            } else {
                await api.post('/students', submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
                alert('Admission form submitted successfully!');

                // Reset form only if not editing
                setFormData({
                    full_name: '', phone: '', email: '', passport_no: '',
                    date_of_birth: '', sex: '', nationality: '',
                    father_name: '', father_contact: '',
                    mother_name: '', mother_contact: '',
                    permanent_address: '', temporary_address: '',
                    guardian_name: '', guardian_relation: '', guardian_contact: '',
                    abroad_study_country: '', visa_type: '', passed_out_year: '', gpa: '',
                    preparation_classes: [],
                    form_fee_paid: false,
                    photo: null
                });
                setPhotoPreview(null);
            }

            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            const serverError = err.response?.data?.details?.[0] || err.response?.data?.error || err.message || 'Error submitting form.';
            setError(serverError);
            alert('Error: ' + serverError);
        }
    };

    return (
        <div className="mt-8 space-y-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-6 bg-gradient-to-b from-gray-50 to-white relative w-full print:shadow-none print:border-none print:p-0 print:m-0 print:bg-white print-high-fidelity">
            <style dangerouslySetInnerHTML={{
                __html: `
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; font-size: 10pt !important; }
                    .card { box-shadow: none !important; border: 1px solid #f3f4f6 !important; }
                    .input-field { 
                        appearance: none !important;
                        border: 1px solid #e5e7eb !important; 
                        background: white !important; 
                        color: #111827 !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        height: 2.2rem !important;
                        padding: 0 0.5rem !important;
                        font-family: inherit !important;
                    }
                    div, h1, h2, h3, h4, p, span, label {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .bg-blue-50, .bg-indigo-50, .bg-pink-50, .bg-gray-50, .bg-amber-50 {
                        background-color: inherit !important;
                        border: 1px solid #f3f4f6 !important;
                    }
                }
            `}} />

            {error && <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded shadow-sm no-print">{error}</div>}

            <form onSubmit={handleSubmit}>
                {/* Header (Branded as Web UI) */}
                <div className="flex flex-col md:flex-row justify-between items-center md:items-start mb-6 border-b border-gray-200 pb-6 gap-6 md:gap-0">
                    <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
                        <img src={process.env.PUBLIC_URL + '/logo.png'} alt="CSK Details" className="w-24 h-24 object-contain rounded drop-shadow-sm bg-white p-1 border border-gray-100" />
                        <div>
                            <h2 className="text-2xl md:text-3xl font-extrabold text-blue-900 tracking-tight">C.S.K Language & Consultancy P. Ltd.</h2>
                            <p className="text-xs md:text-sm font-semibold text-gray-500 uppercase tracking-widest mt-1">STUDENT ADMISSION FORM</p>
                            <p className="text-xs text-gray-400 mt-1">Regd. No.: 48708/064/065</p>
                        </div>
                    </div>

                    <div className="flex flex-col items-center">
                        <label className={`cursor-pointer border-2 border-dashed border-gray-300 w-28 h-36 flex flex-col items-center justify-center bg-gray-50 rounded bg-center bg-cover hover:border-blue-500 transition-colors group relative overflow-hidden ${isEditMode && !formData.photo ? 'border-indigo-400' : ''}`} style={photoPreview ? { backgroundImage: `url(${photoPreview})` } : {}}>
                            {!photoPreview && (
                                <div className="text-center p-2">
                                    <span className="text-2xl opacity-50 group-hover:opacity-100 transition-opacity">📸</span>
                                    <p className="text-[10px] font-semibold text-gray-400 mt-2 uppercase">Attach 2x2<br />Photo</p>
                                </div>
                            )}
                            <input type="file" accept="image/jpeg, image/jpg, image/png" className="hidden no-print" onChange={handleFileChange} />
                            {photoPreview && (
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity no-print">
                                    <p className="text-white text-xs font-bold font-sans">Change</p>
                                </div>
                            )}
                        </label>
                    </div>
                </div>

                <div className="space-y-8">
                    {/* Personal Details */}
                    <div>
                        <h3 className="text-lg font-bold text-indigo-800 border-b-2 border-indigo-100 pb-2 mb-4 flex items-center gap-2"><span className="text-xl">👤</span> Personal Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            <div>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Full Name <span className="text-red-500 no-print">*</span></label>
                                <input type="text" name="full_name" value={formData.full_name} onChange={handleChange} className={`input-field ${fieldErrors.full_name ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                {fieldErrors.full_name && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.full_name}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Email Address <span className="text-red-500 no-print">*</span></label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} className={`input-field ${fieldErrors.email ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                {fieldErrors.email && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.email}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Phone Number <span className="text-red-500 no-print">*</span></label>
                                <input type="text" name="phone" value={formData.phone} onChange={handleChange} className={`input-field ${fieldErrors.phone ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                {fieldErrors.phone && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.phone}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Passport Number <span className="text-red-500 no-print">*</span></label>
                                <input type="text" name="passport_no" value={formData.passport_no} onChange={handleChange} className={`input-field ${fieldErrors.passport_no ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                {fieldErrors.passport_no && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.passport_no}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Date of Birth <span className="text-red-500 no-print">*</span></label>
                                <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} className={`input-field ${fieldErrors.date_of_birth ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                {fieldErrors.date_of_birth && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.date_of_birth}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Gender <span className="text-red-500 no-print">*</span></label>
                                <select name="sex" value={formData.sex} onChange={handleChange} className={`input-field ${fieldErrors.sex ? 'border-red-500 ring-1 ring-red-500' : ''}`} required>
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Others">Others</option>
                                </select>
                                {fieldErrors.sex && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.sex}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Nationality <span className="text-red-500 no-print">*</span></label>
                                <input type="text" name="nationality" value={formData.nationality} onChange={handleChange} className={`input-field ${fieldErrors.nationality ? 'border-red-500 ring-1 ring-red-500' : ''}`} placeholder="e.g. Nepali" required />
                                {fieldErrors.nationality && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.nationality}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Family Details */}
                    <div>
                        <h3 className="text-lg font-bold text-indigo-800 border-b-2 border-indigo-100 pb-2 mb-4 flex items-center gap-2"><span className="text-xl">👨‍👩‍👦</span> Family Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 print:bg-white print:border-gray-200">
                                <h4 className="font-bold text-blue-800 mb-3 text-sm">Father Details</h4>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Name <span className="text-red-500 no-print">*</span></label>
                                <input type="text" name="father_name" value={formData.father_name} onChange={handleChange} className={`input-field mb-3 bg-white ${fieldErrors.father_name ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                {fieldErrors.father_name && <p className="text-red-500 text-[10px] mb-2 font-bold no-print">{fieldErrors.father_name}</p>}
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Contact Number <span className="text-red-500 no-print">*</span></label>
                                <input type="text" name="father_contact" value={formData.father_contact} onChange={handleChange} className={`input-field bg-white ${fieldErrors.father_contact ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                {fieldErrors.father_contact && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.father_contact}</p>}
                            </div>
                            <div className="bg-pink-50/50 p-4 rounded-xl border border-pink-100 print:bg-white print:border-gray-200">
                                <h4 className="font-bold text-pink-800 mb-3 text-sm">Mother Details</h4>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Name <span className="text-red-500 no-print">*</span></label>
                                <input type="text" name="mother_name" value={formData.mother_name} onChange={handleChange} className={`input-field mb-3 bg-white ${fieldErrors.mother_name ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                {fieldErrors.mother_name && <p className="text-red-500 text-[10px] mb-2 font-bold no-print">{fieldErrors.mother_name}</p>}
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Contact Number <span className="text-red-500 no-print">*</span></label>
                                <input type="text" name="mother_contact" value={formData.mother_contact} onChange={handleChange} className={`input-field bg-white ${fieldErrors.mother_contact ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                {fieldErrors.mother_contact && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.mother_contact}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Address & Guardianship */}
                    <div>
                        <h3 className="text-lg font-bold text-indigo-800 border-b-2 border-indigo-100 pb-2 mb-4 flex items-center gap-2"><span className="text-xl">🏠</span> Address & Guardianship</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                            <div>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Permanent Address <span className="text-red-500 no-print">*</span></label>
                                <input type="text" name="permanent_address" value={formData.permanent_address} onChange={handleChange} className={`input-field ${fieldErrors.permanent_address ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                {fieldErrors.permanent_address && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.permanent_address}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Temporary Address <span className="text-red-500 no-print">*</span></label>
                                <input type="text" name="temporary_address" value={formData.temporary_address} onChange={handleChange} className={`input-field ${fieldErrors.temporary_address ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                {fieldErrors.temporary_address && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.temporary_address}</p>}
                            </div>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl print:bg-white print:border-gray-200">
                            <h4 className="font-semibold text-gray-700 mb-3 text-sm flex items-center gap-2"><span className="text-lg">🛡️</span> Guardianship (If staying with others)</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div>
                                    <label className="block text-sm font-semibold mb-1 text-gray-700">Guardian's Name</label>
                                    <input type="text" name="guardian_name" value={formData.guardian_name} onChange={handleChange} className="input-field bg-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold mb-1 text-gray-700">Relation</label>
                                    <input type="text" name="guardian_relation" value={formData.guardian_relation} onChange={handleChange} className="input-field bg-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold mb-1 text-gray-700">Contact Number</label>
                                    <input type="text" name="guardian_contact" value={formData.guardian_contact} onChange={handleChange} className="input-field bg-white" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Academic & Admission Details */}
                    <div>
                        <h3 className="text-lg font-bold text-indigo-800 border-b-2 border-indigo-100 pb-2 mb-4 flex items-center gap-2"><span className="text-xl">🎓</span> Academic & Admission Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            <div>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Target Country <span className="text-red-500 no-print">*</span></label>
                                <select name="abroad_study_country" value={formData.abroad_study_country} onChange={handleChange} className={`input-field ${fieldErrors.abroad_study_country ? 'border-red-500 ring-1 ring-red-500' : ''}`} required>
                                    <option value="">Select Country</option>
                                    {activeCountries.map(c => (
                                        <option key={c.id} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                                {fieldErrors.abroad_study_country && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.abroad_study_country}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1 text-gray-700">Visa Type <span className="text-red-500 no-print">*</span></label>
                                <select name="visa_type" value={formData.visa_type} onChange={handleChange} className={`input-field ${fieldErrors.visa_type ? 'border-red-500 ring-1 ring-red-500' : ''}`} required>
                                    <option value="">Select Visa Type</option>
                                    <option value="Student">Student Visa</option>
                                    <option value="Working">Working Visa</option>
                                </select>
                                {fieldErrors.visa_type && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.visa_type}</p>}
                            </div>
                            {formData.visa_type === 'Student' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1 text-gray-700">Passed Out Year <span className="text-red-500 no-print">*</span></label>
                                        <input type="text" name="passed_out_year" value={formData.passed_out_year} onChange={handleChange} className={`input-field ${fieldErrors.passed_out_year ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                        {fieldErrors.passed_out_year && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.passed_out_year}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1 text-gray-700">GPA / Score <span className="text-red-500 no-print">*</span></label>
                                        <input type="text" name="gpa" value={formData.gpa} onChange={handleChange} className={`input-field ${fieldErrors.gpa ? 'border-red-500 ring-1 ring-red-500' : ''}`} required />
                                        {fieldErrors.gpa && <p className="text-red-500 text-[10px] mt-1 font-bold no-print">{fieldErrors.gpa}</p>}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Preparation Classes (Visible in Web) */}
                    <div className="no-print">
                        <h3 className="text-lg font-bold text-indigo-800 border-b-2 border-indigo-100 pb-2 mb-4 flex items-center gap-2"><span className="text-xl">📚</span> Preparation Classes</h3>
                        <div className="flex flex-wrap gap-4">
                            {['Japanese', 'Korean', 'IELTS'].map(cls => (
                                <label key={cls} className="inline-flex items-center gap-3 text-sm font-semibold text-gray-800 bg-gray-50 border border-gray-200 px-4 py-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                                    <input
                                        type="checkbox"
                                        name="preparation_classes"
                                        value={cls}
                                        onChange={handleChange}
                                        checked={formData.preparation_classes.includes(cls)}
                                        className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                                    />
                                    {cls}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Preparation Classes (Visible in Print) */}
                    <div className="hidden print:block">
                        <h3 className="text-lg font-bold text-indigo-800 border-b-2 border-indigo-100 pb-2 mb-4 flex items-center gap-2"><span className="text-xl">📚</span> Enrolled Classes</h3>
                        <div className="input-field flex items-center w-full h-auto min-h-[2.2rem] py-1">
                            {formData.preparation_classes.length > 0 ? formData.preparation_classes.join(', ') : 'None'}
                        </div>
                    </div>

                    {/* Terms & Signature (Print Only Formal Section) */}
                    <div className="bg-amber-50 p-6 rounded-xl border-l-4 border-amber-500 mt-6 print:bg-white print:border-gray-300">
                        <div className="flex items-center justify-between mb-4 border-b border-amber-200 pb-3 no-print">
                            <span className="font-bold text-amber-900 flex items-center gap-2"><span className="text-xl">📜</span> Terms and Conditions</span>
                            <label className="inline-flex items-center gap-2 text-sm font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 px-4 py-2 rounded-lg cursor-pointer">
                                <input type="checkbox" name="form_fee_paid" onChange={handleChange} checked={formData.form_fee_paid} className="w-5 h-5 text-emerald-600 border-emerald-300 rounded focus:ring-emerald-500 cursor-pointer" />
                                Form Fee Paid
                            </label>
                        </div>
                        <div className="print:block hidden mb-4">
                            <h4 className="font-bold text-gray-800 mb-2 uppercase text-sm underline">Terms & Registration Agreement</h4>
                        </div>
                        <ul className="list-disc pl-6 space-y-2 text-amber-800 text-sm font-medium print:text-gray-700">
                            <li>Classes will run from Sunday to Friday. Attendance is required.</li>
                            <li>If the student stops attending class, the fee is entirely non-refundable.</li>
                            <li>Students should bring their own materials, though course books can be provided for a fee.</li>
                            <li>Classes will only commence after full payment is received.</li>
                            <li>Mutual understanding is expected. This agreement may be terminated without negotiation if policies are broken.</li>
                        </ul>

                        {/* Hidden signature lines that only show in print */}
                        <div className="mt-16 hidden print:flex justify-between items-end">
                            <div className="text-center w-64 border-t border-black pt-2">
                                <p className="text-xs font-bold uppercase tracking-widest">Student's Signature</p>
                                <p className="text-[8pt] text-gray-400 mt-1">Date: {new Date().toLocaleDateString()}</p>
                            </div>
                            <div className="text-center w-64 border-t border-black pt-2">
                                <p className="text-xs font-bold uppercase tracking-widest text-blue-900">For C.S.K Language & Consultancy P. Ltd.</p>
                                <p className="text-[8pt] text-gray-400 mt-1">(Seal & Authorized Sign)</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-8 flex justify-end gap-4 border-t border-gray-200 pt-6 no-print">
                    {onCancel && (
                        <button type="button" onClick={onCancel} className="btn-secondary px-6 text-slate-700">
                            Cancel
                        </button>
                    )}
                    <button type="submit" className="btn-primary px-8">
                        {isEditMode ? 'Update Profile' : 'Submit Admission'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdmissionForm;
